/**
 * Purchase Management Service
 * 
 * Service for managing suppliers, purchase orders, purchase bills, and purchase returns
 */

import apiClient from './api';
import {
  Supplier,
  PurchaseOrder,
  PurchaseBill,
  PurchaseReturn,
  ChargeType,
  DiscountRule,
  CreateSupplierRequest,
  UpdateSupplierRequest,
  CreatePurchaseOrderRequest,
  UpdatePurchaseOrderRequest,
  GoodsReceiptRequest,
  CreatePurchaseBillRequest,
  SupplierListParams,
  PurchaseOrderListParams,
  PurchaseBillListParams,
  PurchaseReturnListParams,
  SupplierListResponse,
  PurchaseOrderListResponse,
  PurchaseBillListResponse,
  PurchaseReturnListResponse,
  PurchaseDashboardData,
  SupplierPerformance,
} from '../types/purchase';

export class PurchaseService {
  // Helper function to convert snake_case to camelCase
  private static toCamelCase(obj: any): any {
    if (obj === null || obj === undefined) return obj;
    if (Array.isArray(obj)) return obj.map(item => this.toCamelCase(item));
    if (typeof obj !== 'object' || obj instanceof Date) return obj;

    const camelCaseObj: any = {};
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        const camelKey = key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
        camelCaseObj[camelKey] = this.toCamelCase(obj[key]);
      }
    }
    return camelCaseObj;
  }

  // Helper function to convert camelCase to snake_case
  private static toSnakeCase(obj: any): any {
    if (obj === null || obj === undefined) return obj;
    if (Array.isArray(obj)) return obj.map(item => this.toSnakeCase(item));
    if (typeof obj !== 'object') return obj;

    const snakeCaseObj: any = {};
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        const snakeKey = key.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
        snakeCaseObj[snakeKey] = this.toSnakeCase(obj[key]);
      }
    }
    return snakeCaseObj;
  }

  // Supplier Management
  static async getSuppliers(params?: SupplierListParams): Promise<SupplierListResponse> {
    const response = await apiClient.get('/admin/suppliers', { params });
    console.log('Suppliers API response:', response.data);
    // Transform snake_case to camelCase
    return {
      items: response.data.items?.map((supplier: any) => this.toCamelCase(supplier)) || [],
      total: response.data.total,
      page: response.data.page || 1,
      totalPages: response.data.total_pages || response.data.pages || 1,
      limit: response.data.limit
    };
  }

  static async getSupplier(id: string): Promise<Supplier> {
    const response = await apiClient.get(`/admin/suppliers/${id}`);
    return this.toCamelCase(response.data);
  }

  static async createSupplier(data: CreateSupplierRequest): Promise<Supplier> {
    const transformedData = this.toSnakeCase(data);
    const response = await apiClient.post('/admin/suppliers', transformedData);
    return this.toCamelCase(response.data);
  }

  static async updateSupplier(id: string, data: UpdateSupplierRequest): Promise<Supplier> {
    const transformedData = this.toSnakeCase(data);
    const response = await apiClient.put(`/admin/suppliers/${id}`, transformedData);
    return this.toCamelCase(response.data);
  }

  static async deleteSupplier(id: string): Promise<void> {
    await apiClient.delete(`/admin/suppliers/${id}`);
  }

  static async getSupplierPerformance(id: string): Promise<SupplierPerformance> {
    const response = await apiClient.get(`/admin/suppliers/${id}/performance`);
    return response.data;
  }

  // Purchase Order Management
  static async getPurchaseOrders(params?: PurchaseOrderListParams): Promise<PurchaseOrderListResponse> {
    const response = await apiClient.get('/admin/purchase/orders', { params });
    // Transform snake_case response to camelCase
    const data = response.data;
    return {
      purchase_orders: data.purchase_orders?.map((po: any) => this.toCamelCase(po)) || [],
      total: data.total,
      skip: data.skip,
      limit: data.limit
    };
  }

  static async getPurchaseOrder(id: string): Promise<PurchaseOrder> {
    const response = await apiClient.get(`/admin/purchase/orders/${id}`);
    return this.toCamelCase(response.data);
  }

  static async createPurchaseOrder(data: CreatePurchaseOrderRequest): Promise<PurchaseOrder> {
    const transformedData = this.toSnakeCase(data);
    const response = await apiClient.post('/admin/purchase/orders', transformedData);
    return response.data;
  }

  static async updatePurchaseOrder(id: string, data: UpdatePurchaseOrderRequest): Promise<PurchaseOrder> {
    const transformedData = this.toSnakeCase(data);
    const response = await apiClient.put(`/admin/purchase/orders/${id}`, transformedData);
    return response.data;
  }

  static async deletePurchaseOrder(id: string): Promise<void> {
    await apiClient.delete(`/admin/purchase/orders/${id}`);
  }

  static async sendPurchaseOrder(id: string): Promise<PurchaseOrder> {
    const response = await apiClient.post(`/admin/purchase/orders/${id}/send`);
    return response.data;
  }

  static async acknowledgePurchaseOrder(id: string): Promise<PurchaseOrder> {
    const response = await apiClient.post(`/admin/purchase/orders/${id}/acknowledge`);
    return response.data;
  }

  static async receiveGoods(id: string, data: GoodsReceiptRequest): Promise<PurchaseOrder> {
    const response = await apiClient.post(`/admin/purchase/orders/${id}/receive`, data);
    return response.data;
  }

  static async convertToBill(id: string): Promise<PurchaseBill> {
    const response = await apiClient.post(`/admin/purchase/orders/${id}/convert-to-bill`);
    return response.data;
  }

  static async updatePurchaseOrderStatus(id: string, data: { status: string; notes?: string }): Promise<PurchaseOrder> {
    const response = await apiClient.put(`/admin/purchase/orders/${id}/status`, data);
    return this.toCamelCase(response.data);
  }

  // Purchase Bill Management
  static async getPurchaseBills(params?: PurchaseBillListParams): Promise<PurchaseBillListResponse> {
    // Convert page to skip for backend
    const backendParams: any = { ...params };
    if (params?.page) {
      backendParams.skip = (params.page - 1) * (params.limit || 20);
      delete backendParams.page;
    }
    const response = await apiClient.get('/admin/purchase-bills', { params: backendParams });
    console.log('Purchase bills API response:', response.data);
    return response.data;
  }

  static async getPurchaseBill(id: string): Promise<PurchaseBill> {
    const response = await apiClient.get(`/admin/purchase-bills/${id}`);
    return response.data;
  }

  static async createPurchaseBill(data: CreatePurchaseBillRequest): Promise<PurchaseBill> {
    const transformedData = this.toSnakeCase(data);
    const response = await apiClient.post('/admin/purchase-bills', transformedData);
    return response.data;
  }

  static async updatePurchaseBill(id: string, data: Partial<CreatePurchaseBillRequest>): Promise<PurchaseBill> {
    const transformedData = this.toSnakeCase(data);
    const response = await apiClient.put(`/admin/purchase-bills/${id}`, transformedData);
    return response.data;
  }

  static async deletePurchaseBill(id: string): Promise<void> {
    await apiClient.delete(`/admin/purchase-bills/${id}`);
  }

  static async verifyPurchaseBill(id: string): Promise<PurchaseBill> {
    const response = await apiClient.post(`/admin/purchase-bills/${id}/verify`);
    return response.data;
  }

  static async receiveGoodsForBill(id: string, warehouseId: string): Promise<{
    success: boolean;
    message: string;
    items_received: number;
    total_quantity: number;
    warnings: string[];
  }> {
    const response = await apiClient.post(`/admin/purchase-bills/${id}/receive-goods`, null, {
      params: { warehouse_id: warehouseId }
    });
    return response.data;
  }

  static async recordPayment(
    id: string, 
    amount: number, 
    paymentDate: string, 
    paymentMethod: string = 'cash',
    referenceNumber?: string,
    notes?: string
  ): Promise<PurchaseBill> {
    const response = await apiClient.post(`/admin/purchase-bills/${id}/payment`, {
      amount,
      paymentDate,
      paymentMethod,
      referenceNumber,
      notes,
    });
    return response.data;
  }

  // Purchase Return Management
  static async getPurchaseReturns(params?: PurchaseReturnListParams): Promise<PurchaseReturnListResponse> {
    const response = await apiClient.get('/admin/purchase-returns', { params });
    return {
      purchase_returns: response.data.purchase_returns?.map((ret: any) => this.toCamelCase(ret)) || [],
      total: response.data.total,
      skip: response.data.skip,
      limit: response.data.limit
    };
  }

  static async getPurchaseReturn(id: string): Promise<PurchaseReturn> {
    const response = await apiClient.get(`/admin/purchase-returns/${id}`);
    return this.toCamelCase(response.data);
  }

  // Payload shape follows the real backend schema (purchase_bill_id, items[].purchase_bill_item_id
  // / return_quantity / return_reason) rather than CreatePurchaseReturnRequest, which doesn't match
  // what the API actually accepts.
  static async createPurchaseReturn(data: {
    purchaseBillId: string;
    returnDate: string;
    returnDescription?: string;
    items: { purchaseBillItemId: string; returnQuantity: number; returnReason: string }[];
  }): Promise<PurchaseReturn> {
    const transformedData = this.toSnakeCase(data);
    const response = await apiClient.post('/admin/purchase-returns', transformedData);
    return this.toCamelCase(response.data);
  }

  static async updatePurchaseReturnStatus(id: string, status: string, notes?: string): Promise<PurchaseReturn> {
    const response = await apiClient.put(`/admin/purchase-returns/${id}/status`, {
      status,
      resolution_notes: notes
    });
    return this.toCamelCase(response.data);
  }

  static async processPurchaseReturn(id: string, warehouseId: string): Promise<PurchaseReturn> {
    const response = await apiClient.post(`/admin/purchase-returns/${id}/process`, null, {
      params: { warehouse_id: warehouseId }
    });
    return this.toCamelCase(response.data);
  }

  // Configuration Management
  static async getChargeTypes(): Promise<ChargeType[]> {
    const response = await apiClient.get('/admin/charge-types');
    return response.data;
  }

  static async createChargeType(data: Omit<ChargeType, 'id'>): Promise<ChargeType> {
    const response = await apiClient.post('/admin/charge-types', data);
    return response.data;
  }

  static async updateChargeType(id: string, data: Partial<Omit<ChargeType, 'id'>>): Promise<ChargeType> {
    const response = await apiClient.put(`/admin/charge-types/${id}`, data);
    return response.data;
  }

  static async deleteChargeType(id: string): Promise<void> {
    await apiClient.delete(`/admin/charge-types/${id}`);
  }

  static async getDiscountRules(): Promise<DiscountRule[]> {
    const response = await apiClient.get('/admin/discount-rules');
    return response.data;
  }

  static async createDiscountRule(data: Omit<DiscountRule, 'id'>): Promise<DiscountRule> {
    const response = await apiClient.post('/admin/discount-rules', data);
    return response.data;
  }

  static async updateDiscountRule(id: string, data: Partial<Omit<DiscountRule, 'id'>>): Promise<DiscountRule> {
    const response = await apiClient.put(`/admin/discount-rules/${id}`, data);
    return response.data;
  }

  static async deleteDiscountRule(id: string): Promise<void> {
    await apiClient.delete(`/admin/discount-rules/${id}`);
  }

  // Dashboard and Analytics
  static async getPurchaseDashboard(dateFrom?: string, dateTo?: string): Promise<PurchaseDashboardData> {
    const response = await apiClient.get('/admin/purchase/dashboard', {
      params: { date_from: dateFrom, date_to: dateTo },
    });
    return response.data;
  }

  static async getSupplierPerformanceReport(dateFrom?: string, dateTo?: string): Promise<SupplierPerformance[]> {
    const response = await apiClient.get('/admin/purchase/supplier-performance', {
      params: { dateFrom, dateTo },
    });
    return response.data;
  }

  // Document Generation
  static async generatePurchaseOrderPDF(id: string, template: string = 'tally'): Promise<Blob> {
    const response = await apiClient.get(`/admin/purchase/orders/${id}/pdf`, {
      params: { template },
      responseType: 'blob',
    });
    return response.data;
  }

  static async generatePurchaseBillPDF(id: string, template: string = 'tally'): Promise<Blob> {
    const response = await apiClient.get(`/admin/purchase-bills/${id}/pdf`, {
      params: { template },
      responseType: 'blob',
    });
    return response.data;
  }

  static async generateDebitNotePDF(id: string): Promise<Blob> {
    const response = await apiClient.get(`/admin/purchase-returns/${id}/debit-note-pdf`, {
      responseType: 'blob',
    });
    return response.data;
  }

  // Utility Methods
  static async calculatePurchaseOrderTotal(items: any[], charges?: any): Promise<any> {
    const response = await apiClient.post('/admin/purchase/calculate-total', {
      items,
      charges,
    });
    return response.data;
  }

  static async validateGSTIN(gstin: string): Promise<{ valid: boolean; details?: any }> {
    const response = await apiClient.post('/admin/purchase/validate-gstin', { gstin });
    return response.data;
  }

  static async searchVariants(query: string, supplierId?: string): Promise<any[]> {
    const response = await apiClient.get('/admin/purchase/search-variants', {
      params: { query, supplierId },
    });
    return response.data;
  }

  // Bulk Operations
  static async bulkUpdatePurchaseOrderStatus(ids: string[], status: string): Promise<void> {
    await apiClient.post('/admin/purchase/orders/bulk-update-status', { ids, status });
  }

  static async bulkDeletePurchaseOrders(ids: string[]): Promise<void> {
    await apiClient.post('/admin/purchase/orders/bulk-delete', { ids });
  }

  static async exportPurchaseOrders(params?: PurchaseOrderListParams): Promise<Blob> {
    const response = await apiClient.get('/admin/purchase/orders/export', {
      params,
      responseType: 'blob',
    });
    return response.data;
  }

  static async exportPurchaseBills(params?: PurchaseBillListParams): Promise<Blob> {
    const response = await apiClient.get('/admin/purchase-bills/export', {
      params,
      responseType: 'blob',
    });
    return response.data;
  }

  static async importSuppliers(file: File): Promise<{ success: number; errors: any[] }> {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await apiClient.post('/admin/suppliers/import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  // Supplier Bulk Operations
  static async bulkExportSuppliers(supplier_ids: string[], format: string = 'csv'): Promise<Blob> {
    const response = await apiClient.post('/admin/suppliers/bulk-export', 
      { supplier_ids, format },
      { responseType: 'blob' }
    );
    return response.data;
  }

  static async bulkUpdateSupplierStatus(
    supplier_ids: string[], 
    is_active: boolean
  ): Promise<{ successful: Array<{ id: string }>, failed: Array<{ id: string, error: string }> }> {
    const response = await apiClient.post('/admin/suppliers/bulk-status-update', {
      supplier_ids,
      is_active,
    });
    return response.data;
  }
}

export default PurchaseService;